import {FullTextSearchResult} from "@stores/SearchStore";
import {WorkspaceModelType} from "@stores/WorkspaceStore";
import {RootStoreModel} from "@stores/RootStore";
import {UserSessionState} from "@stores/UserSessionState";
import {logErrorToSentry} from "../utils/errors/logErrorToSentry";
import {registerSearchRequest} from "./metric/metricActions";
import {CrudServices} from "../platform/services/types/ServiceDependencies";

import {queryGqlApi} from "../graphql/queryGqlApi";


import {SEARCH_DOCUMENTS_POSTGRES, SearchResponse, SearchFilters} from '../graphql/searchQueries';


export const searchContent = async (
    services: CrudServices,
    rootStore: RootStoreModel,
    userState: UserSessionState
) => {
    if (rootStore.search.isSearching || rootStore.search.queryPerformed) {
        return;
    }
    try {
        await _searchContent(services, rootStore.search.textInput, rootStore, userState);
        registerSearchRequest(userState.wsServerConnection);
    } catch (error: unknown) {
        logErrorToSentry(error as Error);
    }
}

export const _searchContent = async (
    services: CrudServices,
    textToSearch: string,
    rootStore: RootStoreModel,
    userState: UserSessionState,
) => {
    const workspace = rootStore.view.workspace!;
    if (rootStore.search.isSearching) {
        return;
    }
    rootStore.search.setIsSearching(true);
    
    try {
        // Use new PostgreSQL search via prosemirror-server GraphQL API
        const searchResponse = await _searchContentPostgreSQL(
            textToSearch,
            workspace,
            services
        );
        // Convert PostgreSQL search results to frontend format
        const formattedResults: FullTextSearchResult[] = searchResponse.items.map(result => ({
            id: result.itemId,
            itemId: result.itemId,
            highlight: result.snippet ? {
                content: result.snippet
            } : undefined
        }));
        
        rootStore.search.setFullTextSearchResults(formattedResults);
    } catch (error) {
        console.error('PostgreSQL search failed, falling back to legacy search:', error);
        logErrorToSentry(error as Error);
    }
}

/**
 * New PostgreSQL search implementation via prosemirror-server GraphQL API
 */
const _searchContentPostgreSQL = async (
    textToSearch: string,
    workspace: WorkspaceModelType,
    services: CrudServices
): Promise<SearchResponse> => {
    // Prepare search filters based on workspace view
    const filters: SearchFilters = {};
    
    if (workspace.view === 'trash') {
        filters.trashed = true;
    } else if (workspace.view === 'archive') {
        filters.archived = true;
    } else {
        filters.trashed = false;
        filters.archived = false;
    }
    
    // Make GraphQL request to prosemirror-server using the pgServer endpoint
    const response = await queryGqlApi(
        SEARCH_DOCUMENTS_POSTGRES,
        {
            query: textToSearch,
            workspaceId: workspace.id,
            filters: filters,
            options: {
                limit: 20
            }
        },
        services.dataClientService,
        services.authService,
        'pgServer' // Use the pgServer endpoint for prosemirror-server GraphQL
    );
    
    return response;
}
